import { Controller } from "@hotwired/stimulus"

// Loads branch images with priority: merchant header → branch AI image → first tag image → rescue → placeholder.
// The view deterministically orders the tag-image list per branch (seed = branch.id),
// so the same branch always shows the same image. We deliberately do not deduplicate
// across the page — that would break the per-branch consistency the seed provides.
export default class extends Controller {
  static values = {
    branchId: Number,
    tagImages: Array,
    merchantImageUrl: String,
    aiImageUrl: String,
    rescueImageUrl: String
  }

  static targets = ["image", "placeholder"]

  connect() {
    this.loadBranchImage()
  }

  loadBranchImage() {
    let imageUrl = null

    if (this.hasMerchantImageUrlValue && this.merchantImageUrlValue) {
      imageUrl = this.merchantImageUrlValue
    }

    if (!imageUrl && this.hasAiImageUrlValue && this.aiImageUrlValue) {
      imageUrl = this.aiImageUrlValue
    }

    if (!imageUrl && this.hasTagImagesValue && this.tagImagesValue.length > 0) {
      imageUrl = this.tagImagesValue[0]
    }

    if (!imageUrl && this.hasRescueImageUrlValue && this.rescueImageUrlValue) {
      imageUrl = this.rescueImageUrlValue
    }

    if (imageUrl) {
      this.showImage(imageUrl)
    } else {
      this.showPlaceholder()
    }
  }

  showImage(url, { retried = false } = {}) {
    if (this.hasImageTarget) {
      // Attach onerror BEFORE assigning src. If src is served from cache and the
      // resource fails synchronously (e.g. a cached ActiveStorage 302 pointing at
      // an expired S3 signature -> 403), assigning src first would fire the error
      // before the handler exists and the fallback chain would never run.
      this.imageTarget.onerror = () => {
        // A cached redirect to an expired signed URL 403s. Re-request once with a
        // cache-buster so the ActiveStorage redirect endpoint mints a fresh URL,
        // bypassing the stale cached 302, before falling through to other sources.
        if (!retried && this.isActiveStorageUrl(url)) {
          this.showImage(this.withCacheBuster(url), { retried: true })
          return
        }
        // If merchant image failed, fall back to the branch AI image, then tag images.
        if (this.stripCacheBuster(url) === this.merchantImageUrlValue) {
          if (this.hasAiImageUrlValue && this.aiImageUrlValue) {
            this.showImage(this.aiImageUrlValue)
            return
          }
          if (this.hasTagImagesValue && this.tagImagesValue.length > 0) {
            this.showImage(this.tagImagesValue[0])
            return
          }
        }
        // If the branch AI image failed, fall back to the first tag image.
        if (this.stripCacheBuster(url) === this.aiImageUrlValue && this.hasTagImagesValue && this.tagImagesValue.length > 0) {
          this.showImage(this.tagImagesValue[0])
          return
        }
        // Otherwise fall back to rescue.
        if (this.stripCacheBuster(url) !== this.rescueImageUrlValue && this.hasRescueImageUrlValue && this.rescueImageUrlValue) {
          this.showImage(this.rescueImageUrlValue)
        } else {
          this.showPlaceholder()
        }
      }

      this.imageTarget.src = url
      this.imageTarget.classList.remove('hidden')
    }

    if (this.hasPlaceholderTarget) {
      this.placeholderTarget.classList.add('hidden')
    }
  }

  isActiveStorageUrl(url) {
    return typeof url === 'string' && url.includes('/rails/active_storage/')
  }

  withCacheBuster(url) {
    return url + (url.includes('?') ? '&' : '?') + '_r=' + Date.now()
  }

  stripCacheBuster(url) {
    return typeof url === 'string' ? url.replace(/[?&]_r=\d+/, '') : url
  }

  showPlaceholder() {
    if (this.hasImageTarget) {
      this.imageTarget.classList.add('hidden')
    }

    if (this.hasPlaceholderTarget) {
      this.placeholderTarget.classList.remove('hidden')
    }
  }
}
